import React, { FormEvent, useEffect, useState } from 'react';

import { t } from '@lingui/macro';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button, InputPassword } from '@olist/design-system';

import { PageLayout } from 'components';
import { ButtonWrapper, Logos, Title } from 'components/general';
import { OlistEmail } from 'components/icons/appLogos';
import useMixPanel from 'hooks/use-mixpanel';

const ACCOUNT_API_URL = process.env.REACT_APP_ACCOUNTS_API;
const headers = {
  'Content-Type': 'application/json',
};

export default function ResetPassword() {
  const initialsValue = {
    password: '',
    confirm_password: '',
    reset_token: '',
  };
  const navigate = useNavigate();
  const [values, setValues] = useState(initialsValue);
  const [searchParams] = useSearchParams();
  const [errorMessagePassword, setErrorMessagePassword] = useState('');
  const [errorMessageConfirmPassword, setErrorMessageConfirmPassword] = useState('');
  const { track } = useMixPanel(false);

  const tokenParam = searchParams.get('token');

  if (!tokenParam) {
    throw new Error('token not found');
  }

  function isError() {
    setErrorMessagePassword('');
    setErrorMessageConfirmPassword('');

    if (values.password.length < 6) {
      setErrorMessagePassword(t`The new password must be at least 6 characters long.`);
      return true;
    }

    if (values.confirm_password !== values.password) {
      setErrorMessageConfirmPassword(t`Password confirmation does not match.`);
      return true;
    }
    return false;
  }

  useEffect(() => {
    const body = JSON.stringify({ reset_token: tokenParam });

    axios
      .post(`${ACCOUNT_API_URL}/v1/reset-user-password/check`, body, { headers })
      .then((response: any) => {
        if (response.data && !response.data.is_active) {
          navigate('/auth/reset-password-invalid');
        }
      });
  }, [navigate, tokenParam]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (!isError()) {
      values.reset_token = tokenParam;
      const body = JSON.stringify({ reset_token: tokenParam, password: values.password });

      axios
        .put(`${ACCOUNT_API_URL}/v1/reset-user-password/`, body, { headers })
        .then(() => {
          track('Reset Password - Clicked on Change Password');
          navigate('/auth/reset-password-success');
        })
        .catch((error: any) => {
          if (error.response.status === 400) {
            navigate(`/auth/reset-password-invalid?type=invalid_pass&token=${tokenParam}`);
          } else {
            navigate('/auth/reset-password-invalid?type=invalid_token');
          }
        });
    }
  };

  const onChange = (ev: FormEvent) => {
    const { name, value } = ev.target as HTMLInputElement;
    setValues({ ...values, [name]: value });
  };

  function hasErrorPassword() {
    return errorMessagePassword.length > 0;
  }

  function hasErrorConfirmPassword() {
    return errorMessageConfirmPassword.length > 0;
  }

  return (
    <PageLayout>
      <section>
        <Logos>
          <OlistEmail />
        </Logos>
        <Title>{t`set your new password`}</Title>

        <form onSubmit={handleSubmit}>
          <div>
            <InputPassword
              required
              id="password"
              name="password"
              label={t`new password`}
              onChange={onChange}
              feedback={hasErrorPassword() ? 'error' : ''}
              helperText={errorMessagePassword || ''}
            />
          </div>
          <div>
            <InputPassword
              required
              id="confirm_password"
              name="confirm_password"
              label={t`confirm password`}
              onChange={onChange}
              feedback={hasErrorConfirmPassword() ? 'error' : ''}
              helperText={errorMessageConfirmPassword || ''}
            />
          </div>
          <ButtonWrapper>
            <Button fixed variation="primary" type="submit" size="medium">
              {t`redefine password`}
            </Button>
          </ButtonWrapper>
        </form>
      </section>
    </PageLayout>
  );
}
